import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiKey } from '@xeetr/types/models'
import { useContext, useState } from 'react'

import { ActiveTenantContext } from '../context/common/Context'
import { queryKeys } from '../lib/queryKeys'
import { ApiKeyService } from '../services/ApiKey.service'

import { useErrorHandler } from './common/useErrorHandler'
import { useTenantOptionalService } from './common/useServiceFactory'
import { useAuth } from './useAuth'

import { showErrorToastUnlessPermissionDenied, showToast } from '@/utils/toastMessage'

interface UseApiKeysReturn {
  keys: ApiKey[]
  generatedKey: string | null
  isLoading: boolean

  load: () => void
  generate: (description: string, permissions?: string[]) => Promise<void>
  update: (id: string, permissions: string[]) => Promise<void>
  remove: (id: string) => Promise<void>
  clearGeneratedKey: () => void
}

export const useApiKeys = (): UseApiKeysReturn => {
  const { getAccessTokenSilently } = useAuth()
  const { tenantId, tenant } = useContext(ActiveTenantContext)
  const queryClient = useQueryClient()

  const [generatedKey, setGeneratedKey] = useState<string | null>(null)

  const getService = useTenantOptionalService(ApiKeyService)

  const apiKeysQuery = useQuery({
    queryKey: queryKeys.apiKeys(tenantId || ''),
    queryFn: async () => {
      if (!tenantId) return []
      const service = await getService()
      const response = await service.getApiKeys()
      return response.apiKeys
    },
    enabled: false,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  })

  useErrorHandler(
    apiKeysQuery.isError,
    'Fehler beim Laden der API-Schlüssel.',
    'error',
    apiKeysQuery.error
  )

  const generateMutation = useMutation({
    mutationFn: async ({
      description,
      permissions,
    }: {
      description: string
      permissions?: string[]
    }) => {
      const service = await getService()
      return service.generateApiKey(description, permissions)
    },
    onSuccess: (response) => {
      if (!tenantId) return

      setGeneratedKey(response.apiKey)

      queryClient.setQueryData<ApiKey[]>(queryKeys.apiKeys(tenantId), (old = []) => [
        ...old,
        {
          id: response.id,
          description: response.description,
          permissions: response.permissions,
          issuedAt: response.issuedAt,
          expiresAt: undefined,
        },
      ])

      showToast({ severity: 'success', detail: 'API-Schlüssel wurde generiert' })
    },
    onError: (error) => {
      console.error('Error generating API key:', error)
      showErrorToastUnlessPermissionDenied(error, {
        severity: 'error',
        detail: 'Fehler beim Generieren des API-Schlüssels',
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, permissions }: { id: string; permissions: string[] }) => {
      if (!tenant) throw new Error('No tenant')

      const token = await getAccessTokenSilently()
      const service = new ApiKeyService(token, tenant.id, tenant.type)
      return service.updateApiKey(id, permissions)
    },
    onSuccess: (_, variables) => {
      if (!tenantId) return

      queryClient.setQueryData<ApiKey[]>(queryKeys.apiKeys(tenantId), (old = []) =>
        old.map((key) =>
          key.id === variables.id ? { ...key, permissions: variables.permissions } : key
        )
      )

      showToast({ severity: 'success', detail: 'API-Schlüssel-Berechtigungen wurden aktualisiert' })
    },
    onError: (error) => {
      console.error('Error updating API key:', error)
      showErrorToastUnlessPermissionDenied(error, {
        severity: 'error',
        detail: 'Fehler beim Aktualisieren der Berechtigungen',
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const service = await getService()
      return service.deleteApiKey(id)
    },
    onSuccess: (_, id) => {
      if (!tenantId) return

      queryClient.setQueryData<ApiKey[]>(queryKeys.apiKeys(tenantId), (old = []) =>
        old.filter((key) => key.id !== id)
      )

      showToast({ severity: 'success', detail: 'API-Schlüssel wurde gelöscht' })
    },
    onError: (error) => {
      console.error('Error deleting API key:', error)
      showErrorToastUnlessPermissionDenied(error, {
        severity: 'error',
        detail: 'Fehler beim Löschen des API-Schlüssels',
      })
    },
  })

  const isLoading =
    apiKeysQuery.isLoading ||
    generateMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending

  return {
    keys: apiKeysQuery.data || [],
    generatedKey,
    isLoading,

    load: () => {
      apiKeysQuery.refetch()
    },
    generate: async (description: string, permissions?: string[]) => {
      await generateMutation.mutateAsync({ description, permissions })
    },
    update: async (id: string, permissions: string[]) => {
      await updateMutation.mutateAsync({ id, permissions })
    },
    remove: async (id: string) => {
      await deleteMutation.mutateAsync(id)
    },
    clearGeneratedKey: () => {
      setGeneratedKey(null)
    },
  }
}
